import { faArrowUpRightFromSquare, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { faExpandAlt, faToolbox } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import MaintenanceHealthBadge from 'src/components/maintenance/MaintenanceHealthBadge';
import StatusColorCode from 'src/components/status-color-dot';
import TruncatedTitle from 'src/components/TruncatedTitle';
import { STATUS_COLOR_CODE_TYPES, TOOLING_STOCK_STATUS_VERBOSE } from 'src/utils/constants';
import routes from 'src/utils/routes';
import { getShortUuid, getUuid } from 'src/utils/url';

export const ToolingStockCardSmall = ({ toolingStock, toolingType, togglePreview }) => {
  const toolingStockId = getShortUuid(toolingStock.uri);
  const toolUuid = getUuid(toolingStock.uri);
  const toolingStockViewUri = `${routes.toolRoute}/${toolUuid}`;

  return (
    <div className='tooling-stock-card-new__card card-base' onClick={togglePreview}>
      <div className='tooling-stock-card-new__card-header'>
        <div className='tooling-stock-card-new__card-left card-left'>
          <div className='tooling-stock-card-new__icon icon-container'>
            <FontAwesomeIcon icon={faToolbox} />
          </div>
          <div className='run-workstation-body__info info-section'>
            <div className='tooling-stock-card-new__title-row'>
              <TruncatedTitle
                title={toolingStock.name}
                titleClassName='tooling-stock-card-new__title-text'
                titleAdditionalTextNoTruncate={`(${toolingStockId})`}
                maxTextLength={30}
                maxTextLengthMobile={25}
              />
              <Link
                to={toolingStockViewUri}
                className='tooling-stock-card-new__title-link'
                aria-label='Open tooling stock view'
                title='Open tooling stock view'
                onClick={event => {
                  event.stopPropagation();
                }}
              >
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              </Link>
            </div>
            <div className='tooling-stock-card-new__subtitle subtitle-text'>
              {toolingStockId} | {toolingType.name}
            </div>
          </div>
        </div>
        <div className='tooling-stock-card-new__close'>
          <FontAwesomeIcon icon={faExpandAlt} />
        </div>
      </div>
    </div>
  );
};

ToolingStockCardSmall.propTypes = {
  toolingStock: PropTypes.shape({
    status: PropTypes.string,
    uri: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  toolingType: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  togglePreview: PropTypes.func,
};

ToolingStockCardSmall.defaultProps = {
  togglePreview: null,
};

export const ToolingStockCardPreview = ({
  toolingStock,
  toolingType,
  location,
  subLocation,
  expanded,
  setExpanded,
  customFieldContent,
  customFieldHeaderContent,
  onOverlayToggle,
  isMaintenanceEnabled,
}) => {
  const togglePreview = setExpanded ? () => setExpanded(prev => !prev) : null;
  return expanded ? (
    <ToolingStockCardFull
      location={location}
      toolingType={toolingType}
      subLocation={subLocation}
      toolingStock={toolingStock}
      togglePreview={togglePreview}
      customFieldContent={customFieldContent}
      customFieldHeaderContent={customFieldHeaderContent}
      isMaintenanceEnabled={isMaintenanceEnabled}
      onOverlayToggle={onOverlayToggle}
    />
  ) : (
    <ToolingStockCardSmall
      toolingStock={toolingStock}
      toolingType={toolingType}
      togglePreview={togglePreview}
    />
  );
};

ToolingStockCardPreview.defaultProps = {
  expanded: false,
  customFieldContent: null,
  customFieldHeaderContent: null,
  onOverlayToggle: null,
};

ToolingStockCardPreview.propTypes = {
  toolingStock: PropTypes.shape({
    status: PropTypes.string,
    uri: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  toolingType: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  location: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  subLocation: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  expanded: PropTypes.bool,
  setExpanded: PropTypes.func,
  customFieldContent: PropTypes.node,
  customFieldHeaderContent: PropTypes.node,
  onOverlayToggle: PropTypes.func,
  isMaintenanceEnabled: PropTypes.bool,
};

export const ToolingStockCardFull = ({
  toolingStock,
  toolingType,
  location,
  subLocation,
  togglePreview,
  customFieldContent,
  customFieldHeaderContent,
  onOverlayToggle,
  isMaintenanceEnabled,
}) => {
  const [showToolDataOverlay, setShowToolDataOverlay] = useState(false);

  useEffect(() => {
    if (!onOverlayToggle) return;
    onOverlayToggle();
  }, [showToolDataOverlay, onOverlayToggle]);

  if (!toolingStock || !toolingType) return null;

  const toolingStockId = getShortUuid(toolingStock.uri);
  const locationLabel = [location, subLocation].filter(Boolean).join(' · ');
  const toolUuid = getUuid(toolingStock.uri);
  const toolingStockViewUri = `${routes.toolRoute}/${toolUuid}`;

  return (
    <div className='tooling-stock-card-new__card card-base'>
      <div className='tooling-stock-card-new__card-header'>
        <div className='tooling-stock-card-new__card-left card-left'>
          <div className='tooling-stock-card-new__icon icon-container'>
            <FontAwesomeIcon icon={faToolbox} />
          </div>
          <div className='run-workstation-body__info info-section align-items-start'>
            <div className='tooling-stock-card-new__title-row'>
              <TruncatedTitle
                title={toolingStock.name}
                titleClassName='tooling-stock-card-new__title-text'
                titleAdditionalTextNoTruncate={`(${toolingStockId})`}
                maxTextLength={30}
                maxTextLengthMobile={25}
              />
              <Link
                to={toolingStockViewUri}
                className='tooling-stock-card-new__title-link'
                aria-label='Open tooling stock view'
                title='Open tooling stock view'
              >
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              </Link>
            </div>
            <div className='tooling-stock-card-new__subtitle subtitle-text'>
              {toolingStockId} | {toolingType.name}
            </div>
            {isMaintenanceEnabled && (
              <MaintenanceHealthBadge
                equipmentUri={toolingStock.uri}
                className='mt-2 align-self-start'
                linkClassName='align-self-start'
                maintenanceRoute={routes.toolMaintenance}
              />
            )}
          </div>
        </div>
        {togglePreview && (
          <div className='tooling-stock-card-new__close' onClick={togglePreview}>
            <FontAwesomeIcon icon={faExpandAlt} />
          </div>
        )}
      </div>
      <div className='tooling-stock-card-new__actions'>
        {locationLabel ? (
          <OverlayTrigger
            placement='top'
            overlay={<Tooltip id='location-tooltip'>Location</Tooltip>}
          >
            <div className='run-workstation-materials__badge tooling-stock-card-new__location-badge'>
              <FontAwesomeIcon
                icon={faLocationDot}
                className='tooling-stock-card-new__button-icon'
              />
              <span>{locationLabel}</span>
            </div>
          </OverlayTrigger>
        ) : null}
        <OverlayTrigger placement='top' overlay={<Tooltip id='tooltip'>Tool Status</Tooltip>}>
          <div className='run-workstation-materials__badge run-workstation-materials__badge--material'>
            <StatusColorCode
              status={toolingStock.status}
              type={STATUS_COLOR_CODE_TYPES.TOOLING_STOCK_STATUS}
              sizeClassName='small'
            />
            <span>{TOOLING_STOCK_STATUS_VERBOSE[toolingStock.status]}</span>
          </div>
        </OverlayTrigger>
        <button
          type='button'
          className='tooling-stock-card-new__button'
          onClick={() => setShowToolDataOverlay(prev => !prev)}
        >
          {showToolDataOverlay ? 'Hide Tool Data' : 'Tool Data'}
        </button>
      </div>
      {showToolDataOverlay ? (
        <div className='tooling-stock-card-new__overlay'>
          <div className='tooling-stock-card-new__overlay-header'>
            <span>Tool Data</span>
            {customFieldHeaderContent ? (
              <div className='tooling-stock-card-new__overlay-header-meta'>
                {customFieldHeaderContent}
              </div>
            ) : null}
          </div>
          <div className='tooling-stock-card-new__overlay-body'>
            {customFieldContent || (
              <div className='tooling-stock-card-new__custom-fields'>
                No custom tool data available
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

ToolingStockCardFull.defaultProps = {
  togglePreview: null,
  customFieldContent: null,
  customFieldHeaderContent: null,
  onOverlayToggle: null,
};

ToolingStockCardFull.propTypes = {
  toolingStock: PropTypes.shape({
    status: PropTypes.string,
    uri: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  toolingType: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  location: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  subLocation: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  togglePreview: PropTypes.func,
  customFieldContent: PropTypes.node,
  customFieldHeaderContent: PropTypes.node,
  onOverlayToggle: PropTypes.func,
  isMaintenanceEnabled: PropTypes.bool,
};
